import { node } from 'prop-types';
import React from 'react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            isPromiseRejection: false
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        console.error(
            'ErrorBoundary caught rendering error:',
            error,
            errorInfo
        );
    }

    handleGlobalError = (event) => {
        event.preventDefault(); // Stops the Create React App red overlay from appearing
        console.error('Global uncaught error detected:', event.error);
        this.setState({
            hasError: true,
            error:
                event.error ||
                new Error(
                    event.message || 'An uncaught runtime error occurred'
                ),
            isPromiseRejection: false
        });
    };

    handlePromiseRejection = (event) => {
        event.preventDefault(); // Stops the Create React App red overlay from appearing
        console.error(
            'Global unhandled promise rejection detected:',
            event.reason
        );
        const reason = event.reason;

        // We only want to intercept genuine API errors or unhandled app rejections
        this.setState({
            hasError: true,
            error:
                reason instanceof Error
                    ? reason
                    : new Error(
                          reason?.message ||
                              (typeof reason === 'string'
                                  ? reason
                                  : 'Unhandled Promise Rejection')
                      ),
            isPromiseRejection: true
        });
    };

    componentDidMount() {
        window.addEventListener('error', this.handleGlobalError);
        window.addEventListener(
            'unhandledrejection',
            this.handlePromiseRejection
        );
    }

    componentWillUnmount() {
        window.removeEventListener('error', this.handleGlobalError);
        window.removeEventListener(
            'unhandledrejection',
            this.handlePromiseRejection
        );
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            isPromiseRejection: false
        });
        window.location.reload();
    };

    handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '/';
    };

    render() {
        const { hasError, error, isPromiseRejection } = this.state;
        const { children } = this.props;

        if (hasError) {
            return (
                <div style={styles.overlay}>
                    {/* Background decorative glows */}
                    <div style={styles.glow1}></div>
                    <div style={styles.glow2}></div>

                    <div style={styles.container}>
                        <div style={styles.iconContainer}>
                            <svg
                                style={styles.icon}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                            </svg>
                        </div>

                        <h1 style={styles.title}>
                            {isPromiseRejection
                                ? 'Network & API Exception'
                                : 'Application Crash Detected'}
                        </h1>

                        <p style={styles.subtitle}>
                            An unexpected error occurred. Don't worry, your
                            session data is safe. You can reload the application
                            or try logging in again.
                        </p>

                        {error && (
                            <div style={styles.errorBox}>
                                <div style={styles.errorHeader}>
                                    Error Details
                                </div>
                                <pre style={styles.errorDetails}>
                                    {error.stack ||
                                        error.message ||
                                        String(error)}
                                </pre>
                            </div>
                        )}

                        <div style={styles.buttonGroup}>
                            <button
                                onClick={this.handleReset}
                                style={{
                                    ...styles.button,
                                    ...styles.primaryButton
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#4f46e5';
                                    e.target.style.transform =
                                        'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#6366f1';
                                    e.target.style.transform = 'translateY(0)';
                                }}
                            >
                                Reload Application
                            </button>
                            <button
                                onClick={this.handleLogout}
                                style={{
                                    ...styles.button,
                                    ...styles.secondaryButton
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor =
                                        'rgba(255, 255, 255, 0.12)';
                                    e.target.style.transform =
                                        'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor =
                                        'rgba(255, 255, 255, 0.08)';
                                    e.target.style.transform = 'translateY(0)';
                                }}
                            >
                                Reset Session & Login
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return children;
    }
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#0b0f19',
        color: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily:
            'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px',
        overflow: 'hidden',
        zIndex: 99999
    },
    glow1: {
        position: 'absolute',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background:
            'radial-gradient(circle, rgba(239, 68, 68, 0.12) 0%, rgba(239, 68, 68, 0) 70%)',
        top: '10%',
        left: '15%',
        filter: 'blur(40px)',
        pointerEvents: 'none'
    },
    glow2: {
        position: 'absolute',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background:
            'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, rgba(99, 102, 241, 0) 70%)',
        bottom: '10%',
        right: '10%',
        filter: 'blur(50px)',
        pointerEvents: 'none'
    },
    container: {
        width: '100%',
        maxWidth: '640px',
        backgroundColor: 'rgba(17, 24, 39, 0.7)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '24px',
        padding: '40px',
        textAlign: 'center',
        boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        position: 'relative',
        zIndex: 1
    },
    iconContainer: {
        width: '64px',
        height: '64px',
        borderRadius: '16px',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px auto'
    },
    icon: {
        width: '32px',
        height: '32px',
        color: '#ef4444'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        marginBottom: '12px',
        color: '#ffffff',
        letterSpacing: '-0.025em',
        margin: '0'
    },
    subtitle: {
        fontSize: '16px',
        color: '#9ca3af',
        lineHeight: '1.6',
        margin: '16px 0 28px 0'
    },
    errorBox: {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'left',
        marginBottom: '32px',
        maxHeight: '180px',
        overflowY: 'auto'
    },
    errorHeader: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#ef4444',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '8px'
    },
    errorDetails: {
        fontSize: '13px',
        fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        color: '#d1d5db',
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    },
    buttonGroup: {
        display: 'flex',
        gap: '16px',
        justifyContent: 'center',
        flexWrap: 'wrap'
    },
    button: {
        padding: '12px 24px',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: 'none',
        outline: 'none'
    },
    primaryButton: {
        backgroundColor: '#6366f1',
        color: '#ffffff',
        boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.4)'
    },
    secondaryButton: {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#e5e7eb',
        border: '1px solid rgba(255, 255, 255, 0.1)'
    }
};

ErrorBoundary.propTypes = {
    children: node
};

ErrorBoundary.defaultProps = {
    children: {}
};

export default ErrorBoundary;
